import {
  Avatar,
  Grid,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import CustomDataGrid from '../../../common/components/dataGrid/CustomDataGrid'
import { Add as AddIcon } from '@mui/icons-material'
import DashboardLayout from '../../../common/layouts/DashboardLayout'
import { styled } from '@mui/system'

import {
  setColumnFilters,
  setDirectCustomersList,
} from '../../../store/slice/homeSlice'
import StatusChip from '../../../common/components/mui/Chips'
import { CustomerColumn } from '../../../common/components/dataGrid/columns/HomeColumns'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  executeWithTryCatch,
  isResponseSuccess,
  normalizeApiResponse,
} from '../../../helpers/HelperFunctions'
import { useHomeRedux } from '../Home'
import {
  COMPANY_STATUS,
  COMPANY_STATUS_OBJ,
  TOAST_MESSAGES,
} from '../../../helpers/Constants'
import { useToastContext } from '../../../App'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { motion } from 'framer-motion'
import ConfirmationModal from '../../../common/components/custom/modal/modal'
import { useCommonRedux } from '../../../store/middlewares/CommonRedux'
import { FlexBox } from '../../../common/styles/styled-components/StyledContainers'
import { api_DeleteCustomerComany } from '../../../api/WebPortalAPI'
import { AltigenLogoFlipLoader } from '../../../common/components/loaders/loader'
import { setIsLoading } from '../../../store/slice/commonSlice'
import { toggleColumnFilterValue } from '../../../common/components/dataGrid/utils/toggleColumnFilter'
import { getColumnFilterValues, normalizeStatusKey } from '../../../common/components/dataGrid/utils/getColumnFilterValues'
import { useGridFiltering } from '../../../common/components/dataGrid/utils/useGridFiltering'
import ColumnFilterPopover from '../../../common/components/dataGrid/columns/ColumnFilterPopover'

const FlexContainer = styled('div')({
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'space-evenly',
  alignItems: 'center',
})

const DirectCustomer = (props) => {
  // hooks
  const { dispatch, state } = useHomeRedux()
  const { state: commonState } = useCommonRedux()
  const { toast } = useToastContext()
  const [selectedRow, setSelectedRow] = useState(null)
  const [isOpenDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)

  const [filterAnchor, setFilterAnchor] = useState(null)
  const [activeColumn, setActiveColumn] = useState(null)

  const handleCopy = async (url) => {
    try {
      const permissionStatus = await navigator.permissions.query({
        name: 'clipboard-write',
      })

      if (
        permissionStatus.state === 'granted' ||
        permissionStatus.state === 'prompt'
      ) {
        navigator.clipboard.writeText(url)
        toast.showToast('Text copied to clipboard successfully!')
      } else {
        toast.showToast('Clipboard write permission denied', 'error')
      }
    } catch (err) {
      toast.showToast('Failed to copy text: ', 'error')
    }
  }

  const handleOpenDeleteConfirmation = (row) => {
    setOpenDeleteConfirmation(true)
    setSelectedRow(row)
  }

  const renderCompanyName = (row, value) => {
    return [COMPANY_STATUS.Active, COMPANY_STATUS.ApprovalPending].includes(
      row?.companyStatus
    ) ? (
      <FlexBox>
        <Typography variant="tablePrimary">
          <Link
            underline="none"
            color="primary"
            onClick={() => props?.onRowClick(row)}
          >
            {value}
          </Link>
          <Tooltip title="Copy Login Url">
            <IconButton onClick={() => handleCopy(row?.loginUrl)}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Typography>
        {/* --- Delete Customer part --  */}
        <motion.div whileHover={{ scale: 1.3 }}>
          {commonState.isMaster && (
            <Tooltip title="Delete Customer" placement="right">
              <IconButton
                color="error"
                sx={{ display: 'none' }}
                onClick={() => handleOpenDeleteConfirmation(row)}
              >
                <DeleteOutlineIcon color="error" sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </motion.div>
      </FlexBox>
    ) : (
      <FlexBox>
        <Typography variant="tablePrimary">
          {value}&nbsp;
          <Tooltip title="Copy Signup Url">
            <IconButton onClick={() => handleCopy(row?.signupUrl)}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Typography>
        {/* --- Delete Customer part --  */}
        <motion.div whileHover={{ scale: 1.1 }}>
          {commonState.isMaster && (
            <Tooltip title="Delete Customer" placement="right">
              <IconButton
                color="error"
                sx={{ display: 'none' }}
                onClick={() => handleOpenDeleteConfirmation(row)}
              >
                <DeleteOutlineIcon color="error" sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </motion.div>
      </FlexBox>
    )
  }

  //render company
  const renderCompany = (params) => {
    const { row, value } = params
    return (
      <FlexContainer sx={{ gap: '1rem' }}>
        <Avatar alt="logo" src={row?.logoUrl} sx={{ width: 24, height: 24 }} />
        {renderCompanyName(row, value)}
      </FlexContainer>
    )
  }

  //Render Company Status
  const COMPANY_STATUS_LOOKUP = Object.entries(COMPANY_STATUS).reduce(
    (acc, [key, value]) => {
      acc[normalizeStatusKey(key)] = value
      return acc
    },
    {}
  )

  const resolveStatusId = (statusValue) => {
    if (typeof statusValue === 'number') return statusValue

    const normalized = normalizeStatusKey(statusValue)
    return COMPANY_STATUS_LOOKUP[normalized]
  }

  const renderCompanyStatus = (statusValue) => {
    const statusId = resolveStatusId(statusValue);
    const statusEntry = COMPANY_STATUS_OBJ.find(
      (s) => s.value === statusId
    )

    if (!statusEntry) {
      console.warn('Unknown companyStatus:', statusValue)
      return (
        <StatusChip
          label={String(statusValue)}
          variant="outlined"
          color="default"
          isSharp
        />
      )
    }

    return (
      <StatusChip
        label={statusEntry.name}
        variant="outlined"
        color={statusEntry.color}
        isSharp
      />
    )
  }

  const handleDeleteDirectCustomer = async () => {
    setOpenDeleteConfirmation(false)
    dispatch(setIsLoading(true))
    // TODO: Call API to delete customer
    executeWithTryCatch(async () => {
      const response = await api_DeleteCustomerComany({
        companyId: selectedRow.id,
      })
      if (isResponseSuccess(response)) {
        const directCustomers = [...state.directCustomersList]
        const index = state.directCustomersList.findIndex(
          (customer) => customer.id === selectedRow.id
        )
        if (index === -1) return
        directCustomers.splice(index, 1)
        dispatch(setIsLoading(false))
        setTimeout(
          () => dispatch(setDirectCustomersList(directCustomers)),
          1000
        )
        toast.showToast(TOAST_MESSAGES.CustomerDeleted)
      }
    }, handleDeleteError)
  }

  const handleDeleteError = (error) => {
    dispatch(setIsLoading(false))
    toast.showToast(
      normalizeApiResponse(error)?.message,
      'error'
    )
  }
  /* ---------------- COLUMN FILTERING ---------------- */
  const handleFilterClick = (event, column) => {
    event.stopPropagation()
    setFilterAnchor(event.currentTarget)
    setActiveColumn(column)
  }
 useEffect(() => {
    dispatch(setColumnFilters({}));
  }, []);
  const toggleFilterValue = (value) => {
    dispatch(
      setColumnFilters({
        ...state.columnFilters,
        [activeColumn]: toggleColumnFilterValue(
          state.columnFilters,
          activeColumn,
          value
        ),
      })
    )
  }
  const columnValues = useMemo(() => {
    return getColumnFilterValues({
      rows: state.directCustomersList,
      field: activeColumn,
      statusOptions: COMPANY_STATUS_OBJ,
    })
  }, [activeColumn, state.directCustomersList])

  const columnProps = {
    renderCompany,
    renderCompanyStatus,
    onFilterClick: handleFilterClick,
    activeFilters: state.columnFilters,
  }
  const columns = useMemo(
    () => CustomerColumn(columnProps),
    [columnProps]
  )
  const filteredData = useGridFiltering({
    rows: state.directCustomersList,
    columns: columns,
    searchValue: state.searchValue?.customer,
    columnFilters: state.columnFilters,
    statusField: 'companyStatus',
  })

  const isAnyColumnFiltered = useMemo(
    () =>
      Object.values(state.columnFilters || {}).some(
        (v) => Array.isArray(v) && v.length
      ),
    [state.columnFilters]
  )
  const activeColumnLabel = useMemo(() => {
    if (!activeColumn) return ''

    const col = CustomerColumn(columnProps).find(
      (c) => c.field === activeColumn
    )

    return col?.headerName || activeColumn
  }, [activeColumn])
  return (
    <Grid container>
      {commonState.isLoading && <AltigenLogoFlipLoader />}
      <DashboardLayout
        name="Customer"
        buttonStartIcon={<AddIcon />}
        isShowSearch={props.isShowSearch}
        handleButtonClick={(e) => props?.handleShowCompany(e, 'directCustomer')}
        children={
          <CustomDataGrid
            loading={!Array.isArray(state.directCustomersList)}
            isFiltered={isAnyColumnFiltered}
            filteredCount={filteredData.length}
            totalCount={state.directCustomersList.length}
            noDataText="Customer"
            columns={columns}
            data={filteredData}
            searchValue={state.searchValue?.customer}
          />
        }
      />
      {activeColumn && (
        <ColumnFilterPopover
          anchorEl={filterAnchor}
          title={`Filter by ${activeColumnLabel}`}
          values={columnValues}
          selectedValues={state.columnFilters[activeColumn] || []}
          onChange={toggleFilterValue}
          onClear={() =>
            dispatch(
              setColumnFilters({
                ...state.columnFilters,
                [activeColumn]: [],
              })
            )
          }
          onClose={() => {
            setFilterAnchor(null)
            setActiveColumn(null)
          }}
        />
      )}
      <ConfirmationModal
        isOpen={isOpenDeleteConfirmation}
        title="Delete Customer"
        content={
          <span>
            Are you sure you want to delete <b>{selectedRow?.name}</b> ?
          </span>
        }
        btnOk="Delete Customer"
        btnCancel="Cancel"
        handleClose={() => setOpenDeleteConfirmation(false)}
        handleConfirm={handleDeleteDirectCustomer}
        size="sm"
      />
    </Grid>
  )
}

export default DirectCustomer
